import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AvatarModule } from 'primeng/avatar';
import md5 from 'md5';

/**
 * Shared avatar: OAuth profile photo → Gravatar (by email) → initials fallback.
 * Replaces the copies previously inlined in users, user-profile and the topbar.
 */
@Component({
    selector: 'app-user-avatar',
    standalone: true,
    imports: [CommonModule, AvatarModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (photoUrl(); as url) {
            <p-avatar [image]="url" [size]="size()" shape="circle"></p-avatar>
        } @else {
            <p-avatar [label]="initials()" [size]="size()" shape="circle"></p-avatar>
        }
    `
})
export class UserAvatarComponent {
    /** Any object carrying profilePhotoUrl / email / displayName / username fields. */
    user = input<any>(null);
    size = input<'normal' | 'large' | 'xlarge'>('normal');

    photoUrl = computed<string | null>(() => {
        const user = this.user();
        if (user?.profilePhotoUrl) return user.profilePhotoUrl;
        if (!user?.email) return null;
        // MD5 is required by Gravatar's API, not used for security
        const hash = md5(user.email.toLowerCase().trim());
        return `https://www.gravatar.com/avatar/${hash}?s=80&d=identicon`;
    });

    initials = computed<string>(() => {
        const user = this.user();
        if (!user) return '?';
        const displayName = (user.displayName || user.username || '').trim();
        if (!displayName) return '?';
        const parts = displayName.split(' ').filter((p: string) => p.length > 0);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return displayName.substring(0, Math.min(2, displayName.length)).toUpperCase();
    });
}
